/**
 * Manages logo overlay display on top of the viewer
 */
export class LogoOverlayManager {
    constructor() {
        this.overlay = null;
        this.logoImage = null;
        this.currentPosition = 'top-left';
        this.imageUrl = null;
        this.isVisible = false;

        this.createOverlay();
    }

    /**
     * Create the overlay container
     */
    createOverlay() {
        this.overlay = document.createElement('div');
        this.overlay.id = 'logo-overlay';
        this.overlay.className = 'fixed pointer-events-none z-30'; // Below viewer-controls (z-40) and sidebar (z-999)
        this.overlay.style.display = 'none';

        // Create image element
        this.logoImage = document.createElement('img');
        this.logoImage.className = 'max-w-[100px] max-h-[75px] sm:max-w-[200px] sm:max-h-[150px] object-contain drop-shadow-lg';
        this.logoImage.alt = 'Logo';

        this.overlay.appendChild(this.logoImage);
        document.body.appendChild(this.overlay);

        console.log('✅ Logo overlay created');
    }

    /**
     * Set logo image from file
     * @param {File} file - Image file
     * @returns {Promise<string>} URL of the loaded image
     */
    async setLogo(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const url = e.target.result;
                this.imageUrl = url;
                this.logoImage.src = url;
                this.show();
                resolve(url);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    /**
     * Set logo position
     * @param {string} position - Position (top-left, top-center, top-right, bottom-left, center, bottom-right)
     */
    setPosition(position) {
        this.currentPosition = position;
        this.updatePosition();
    }

    /**
     * Update overlay position based on current position setting
     */
    updatePosition() {
        // Reset all position classes
        this.overlay.className = 'fixed pointer-events-none z-30';

        // Apply position
        // Note: top positions use top-24 to be below viewer-controls and fps-counter (which are at top-6)
        switch (this.currentPosition) {
            case 'top-left':
                this.overlay.classList.add('top-24', 'left-6');
                break;
            case 'top-center':
                this.overlay.classList.add('top-6', 'left-1/2', '-translate-x-1/2');
                break;
            case 'top-right':
                this.overlay.classList.add('top-24', 'right-6');
                break;
            case 'bottom-left':
                this.overlay.classList.add('bottom-6', 'left-6');
                break;
            case 'center':
                this.overlay.classList.add('top-1/2', 'left-1/2', '-translate-x-1/2', '-translate-y-1/2');
                break;
            case 'bottom-right':
                this.overlay.classList.add('bottom-6', 'right-6');
                break;
        }
    }

    /**
     * Show the logo overlay
     */
    show() {
        if (!this.imageUrl) return;

        this.overlay.style.display = 'block';
        this.isVisible = true;
        this.updatePosition();
        console.log(`✅ Logo overlay shown at ${this.currentPosition}`);
    }

    /**
     * Hide the logo overlay
     */
    hide() {
        this.overlay.style.display = 'none';
        this.isVisible = false;
        console.log('Logo overlay hidden');
    }

    /**
     * Remove logo
     */
    removeLogo() {
        this.imageUrl = null;
        this.logoImage.src = '';
        this.hide();
        console.log('✅ Logo removed');
    }

    /**
     * Get current logo data for export
     * @returns {Object|null}
     */
    exportData() {
        if (!this.imageUrl) return null;

        return {
            enabled: this.isVisible,
            imageUrl: this.imageUrl,
            position: this.currentPosition
        };
    }

    /**
     * Load logo data from export
     * @param {Object} data - Logo data
     */
    async loadData(data) {
        if (!data || !data.enabled || !data.imageUrl) {
            this.removeLogo();
            return;
        }

        this.imageUrl = data.imageUrl;
        this.logoImage.src = data.imageUrl;
        this.currentPosition = data.position || 'top-left';

        if (data.enabled) {
            this.show();
        }

        console.log(`✅ Logo loaded at ${this.currentPosition}`);
    }

    /**
     * Cleanup
     */
    dispose() {
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.logoImage = null;
        console.log('✅ Logo overlay disposed');
    }
}
